import React, { Fragment, useEffect } from 'react';
import { useTable } from 'chameleon/ui-stack/ui/base/table';
import { isNil } from 'lodash';
import cn from 'classnames';
import get from 'lodash/get';
import Icon from 'chameleon/ui-stack/ui/base/icon';
import PerformanceBarPopover from 'legacy-src/components/assessment/Results/PerformanceBarPopover';
import A11y from 'chameleon/ui-stack/utilities/a11y';
import {
  getPerformanceStacks,
  getReportToCheckAndTiers,
  getStudentMarksAndColor,
} from './utils';
import SPPAnswerTips from './SPPAnswerTips';

const QuestionsTable = ({
  isManualSort,
  handleSort,
  data,
  headers,
  router,
  userData: { isStudent },
  assessment,
  assignment,
  answerTipsData = [],
  questionsTableSort = {},
  reviewModeFlag,
}) => {
  const columnId = get(questionsTableSort.current, '[0].id');
  const isDesc = get(questionsTableSort.current, '[0].desc');
  let defaultSortBy = [];
  if (columnId) {
    defaultSortBy = [{ id: columnId, desc: isDesc }];
  }

  const {
    Table,
    TableBody,
    TableRow,
    TableColumns,
    TableHeader,
    tableItems,
    sortBy,
  } = useTable({
    data,
    headers,
    isManualSorting: isManualSort,
    initialState: {
      sortBy: defaultSortBy,
    },
  });

  useEffect(() => {
    questionsTableSort.current = sortBy;
    isManualSort && handleSort({ sortBy });
  }, [sortBy]);

  const { params } = router;
  const { isSpp } = assessment;
  const { scoreDisplay } = assignment;

  const questionOrder = Array.from(data, (row) => get(row?.question, '[0]'));

  return (
    <Table includeDefaultRoleProp={false} className="mt-4">
      <TableHeader
        rowClassName={cn('mb-2 border-b border-gray-300', {
          'border-t': isSpp,
        })}
        cellClassName={cn('flex-1 font-normal align-center', {
          'py-3': isSpp,
        })}
      />
      <TableBody includeDefaultRoleProp={false}>
        {tableItems.map((item) => {
          const {
            original: {
              children,
              otherArgs,
              question,
              currentStudentId,
              showSingleResults,
              availableStudents,
              learnosityItemReference,
            },
          } = item;
          const { questionPreviewProps } = otherArgs;
          const {
            params: { ppcPartId },
          } = router;
          const [questionId] = question;
          let questionDetailsURL;
          questionDetailsURL = `/${params.subject}/assessments/results/${params.assignmentId}/questions/${questionId}`;
          if (params.studentId) {
            if (isNil(ppcPartId)) {
              questionDetailsURL = `/${params.subject}/assessments/results/${params.assignmentId}/students/${params.studentId}/questions/${questionId}`;
            } else {
              questionDetailsURL = `/${params.subject}/assessments/results/${params.assignmentId}/students/${params.studentId}/parts/${ppcPartId}/performance/${questionId}`;
            }
          }
          if (isStudent) {
            questionDetailsURL = `/${params.subject}/assessments/results/${params.assignmentId}/performance/${questionId}`;
          }

          const partItem = answerTipsData.find((part) => {
            return part.reference === learnosityItemReference;
          });

          const partialScoreDisplay =
            reviewModeFlag && scoreDisplay === 'partial' && isStudent;

          const learnosityReferenceProps = partialScoreDisplay
            ? {}
            : { 'data-reference': item.learnosityItemReference }; // hide preview player if partial score display

          return (
            <Fragment key={item.id}>
              <A11y>
                <TableRow
                  isDisabled={isSpp || partialScoreDisplay}
                  row={item}
                  key={item.id}
                  data-itemid={item.id}
                  role="link"
                  aria-label={`question-item-${item.id}`}
                  includeDefaultRoleProp={false}
                  className={cn('cursor-pointer hover-question-parent', {
                    'bg-gray-200 hover:bg-gray-200': !isNil(children),
                    'cursor-default': isSpp,
                  })}
                  tabIndex={0}
                  onClick={() => {
                    !partialScoreDisplay &&
                      !isSpp &&
                      isNil(children) &&
                      router.push({
                        pathname: questionDetailsURL,
                        state: {
                          ...(router?.location?.state || {}),
                          questionOrder,
                        },
                      });
                  }}
                  {...learnosityReferenceProps}
                >
                  <TableColumns
                    includeDefaultRoleProp={false}
                    cells={item.cells}
                    className={cn('py-7 flex-1', {
                      'pl-0': isSpp,
                    })}
                  />
                </TableRow>
              </A11y>
              {
                /**
                 * There's a race-condition when the QuestionsTable is opened directly where
                 * answerTipsData has not yet been caclulated, leading to partItem being undefined
                 * adding partItem to the following condition prevents a blank page
                 */
                isSpp && partItem && (
                  <SPPAnswerTips
                    key={partItem?.reference}
                    item={partItem}
                    assignment={assignment}
                  />
                )
              }
              {!isSpp && !isNil(children) ? (
                <tr className="py-8">
                  <td colSpan={item.cells.length}>
                    <div className="divide-y divide-solid divide-gray-400 border-b border-gray-300">
                      {children.map((child) => {
                        const [report, tiers] = getReportToCheckAndTiers({
                          item: child,
                          ...otherArgs,
                        });
                        const { learnosityItemReference } = child;
                        const stacks = getPerformanceStacks(
                          child.learnosityItemReference,
                          report,
                          tiers,
                          availableStudents,
                        );
                        const {
                          learnosityReportScoreParsed,
                          learnosityReportMaxScore,
                          textColor,
                        } = getStudentMarksAndColor({
                          currentStudentId,
                          showSingleResults,
                          learnosityItemReference,
                          report,
                        });

                        let questionDetailsURL;
                        questionDetailsURL = `/${params.subject}/assessments/results/${params.assignmentId}/questions/${questionId}/parts/${child.id}`;
                        if (params.studentId) {
                          questionDetailsURL = `/${params.subject}/assessments/results/${params.assignmentId}/students/${params.studentId}/questions/${questionId}/parts/${child.id}`;
                        }
                        if (isStudent) {
                          questionDetailsURL = `/${params.subject}/assessments/results/${params.assignmentId}/performance/${questionId}/parts/${child.id}`;
                        }
                        const learnosityChildReferenceProps =
                          partialScoreDisplay
                            ? {}
                            : {
                                'data-reference': child.learnosityItemReference,
                              }; // hide preview player if partial score display
                        return (
                          <A11y key={child.id}>
                            <div
                              key={child.id}
                              role="link"
                              aria-label={`question-item-part-${child.id}`}
                              tabIndex={0}
                              onClick={() => {
                                !isSpp &&
                                  !partialScoreDisplay &&
                                  router.push(questionDetailsURL);
                              }}
                              className="ml-25 py-8 flex flex-row hover:bg-blue-100 cursor-pointer space-between hover-question-parent"
                            >
                              <div className="flex self-center">
                                <Icon className="text-gray-500" id="preview" />
                                <span
                                  role="link"
                                  tabIndex="0"
                                  className="ml-4 self-center hover:underline"
                                  data-itemid={child.id}
                                  {...questionPreviewProps}
                                  {...learnosityChildReferenceProps}
                                >
                                  {child.title}
                                </span>
                              </div>
                              <div className="justify-end pr-8">
                                {showSingleResults ? (
                                  isNil(learnosityReportScoreParsed) &&
                                  isNil(learnosityReportMaxScore) ? (
                                    <span className="italic">No Results</span>
                                  ) : (
                                    <span
                                      className={`text-default ${textColor}`}
                                    >
                                      {learnosityReportScoreParsed}/
                                      {learnosityReportMaxScore}
                                    </span>
                                  )
                                ) : (
                                  <PerformanceBarPopover
                                    isWeighed
                                    showScore={false}
                                    stacks={stacks}
                                    contentOffset="0 -2rem 0 0"
                                    studentUrl={`/${params.subject}/assessments/results/${params.assignmentId}/students`}
                                  />
                                )}
                              </div>
                            </div>
                          </A11y>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              ) : null}
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default QuestionsTable;
